import {
  buildRewardItemsSavePayload,
  isMerchantRewardProductSkuSlug,
  normalizeMerchantRewardSavePayload
} from '@/components/rewards/utils/rewardHelpers'
import { applyOfferProductDiscountRewardTypeWhenSkusSelected } from '@/utils/offerHelpers'
import { getProductKey } from '@/utils/productHelpers'
import { slugBelongsTo } from '@/components/rewards/utils/rewardTypeRegistry'

import type {
  LoyaltyReward,
  PartnerProduct,
  RewardCondition,
  RewardConditionOperator,
  RewardConditionType
} from '@/types/types'
import type { AccessCondition, ApplyReductionOn } from '@/types/offer'

export type RequiredProductsConditionWireProducts = {
  items?: { external_id: string; quantity: number }[]
  excluded_external_id?: string[]
}

export type RequiredProductsConditionShowProduct = {
  externalId?: string
  external_id?: string
  quantity?: number
}

export type RequiredProductsConditionWire = {
  type: Extract<RewardConditionType, 'required_products'>
  operator: RewardConditionOperator
  products: RequiredProductsConditionWireProducts | RequiredProductsConditionShowProduct[]
}

export type RewardConditionPayload = RewardCondition | RequiredProductsConditionWire

type RewardTarget = { sku: string; quantity?: number }

export type RewardItemSavePayload = {
  externalId: string
  partnerId: number | null
  name: string
  type: 'product' | 'menu' | 'manual'
  image?: string
  id?: string | number
}

export type RewardSavePayload = {
  id: number | null
  name: string
  description?: string | null
  rewardTypeId: number
  externalId?: string | null
  items?: RewardItemSavePayload[]
  source?: string | null
  usages?: string[]
  storeIds?: number[] | null
  targets?: RewardTarget[]
  value: number | null
  minimumOrderAmount?: number | null
  validityInDays: number | null
  claimLimitQuantity: number | null
  claimLimitDays: number | null
  isForSingleUse: boolean
  conditions: RewardConditionPayload[]
  pictureUrl?: string | null
}

export interface BuildConditionsContext {
  isXForY: boolean
  accessCondition: AccessCondition
  cartItemCount: number | null
  requiredProducts: PartnerProduct[]
  requiredProductQuantities: Record<string, number>
  requiredProductsOperator: RewardConditionOperator
}

export const buildOfferRewardConditions = (
  reward: LoyaltyReward,
  ctx: BuildConditionsContext
): RewardConditionPayload[] => {
  const conditions: RewardConditionPayload[] = []

  if (ctx.isXForY) {
    const buy = reward.value != null ? Number(reward.value) : NaN
    const items = (reward.items ?? [])
      .map((item) => {
        const externalId = item.externalId != null ? String(item.externalId).trim() : ''
        if (!externalId) return null
        return { external_id: externalId, quantity: buy }
      })
      .filter((item): item is { external_id: string; quantity: number } => item != null)

    if (items.length > 0 && Number.isFinite(buy) && buy > 0 && Number.isInteger(buy)) {
      conditions.push({
        type: 'required_products',
        operator: 'contains_any',
        products: { items }
      })
    }
    return conditions
  }

  const cartMinCondition = (reward.conditions ?? []).find((c) => c.type === 'cart_minimum_in_cents')
  if (cartMinCondition?.value != null && cartMinCondition.value > 0) {
    conditions.push({
      type: 'cart_minimum_in_cents',
      operator: 'gte',
      value: cartMinCondition.value
    })
  }

  if (ctx.accessCondition === 'cart_item_count' && ctx.cartItemCount != null && ctx.cartItemCount > 0) {
    conditions.push({ type: 'cart_item_count', operator: 'gte', value: ctx.cartItemCount })
  }

  if (ctx.accessCondition === 'products_required' && Array.isArray(reward.requiredProductExternalIds)) {
    const ids = reward.requiredProductExternalIds.filter((id) => typeof id === 'string' && id.length > 0)

    if (ids.length > 0) {
      const items = ids.map((externalId) => {
        const catalogProduct = ctx.requiredProducts.find((p) => p.externalId.toString() === externalId)
        const key = catalogProduct ? getProductKey(catalogProduct) : `manual-${externalId}`
        return { external_id: externalId, quantity: ctx.requiredProductQuantities[key] ?? 1 }
      })

      conditions.push({
        type: 'required_products',
        operator: ctx.requiredProductsOperator === 'contains_any' ? 'contains_any' : 'contains_all',
        products: { items }
      })
    }
  }

  return conditions
}

export interface BuildPayloadContext extends BuildConditionsContext {
  applyReductionOn: ApplyReductionOn
  rewardTypeIdsBySlug: Record<string, number> | null
}

export const buildRewardPayload = (reward: LoyaltyReward, ctx: BuildPayloadContext): RewardSavePayload | null => {
  if (!reward) return null

  const conditions = buildOfferRewardConditions(reward, ctx)
  const rewardTypeId = reward.rewardType?.id ?? reward.rewardTypeId ?? 1

  const rawStoreIds =
    reward.storeIds && reward.storeIds.length > 0
      ? reward.storeIds.map(Number).filter((id): id is number => Number.isInteger(id) && id > 0)
      : []
  const storeIds = rawStoreIds.length > 0 ? rawStoreIds : undefined

  let value: number | null = null
  let targets: RewardTarget[] | undefined

  if (slugBelongsTo('xForY', reward.rewardType?.slug)) {
    const firstItem = reward.items?.[0]
    const fromItems =
      firstItem?.externalId != null && String(firstItem.externalId).length > 0 ? String(firstItem.externalId) : null
    const fromRequired =
      Array.isArray(reward.requiredProductExternalIds) && reward.requiredProductExternalIds.length > 0
        ? String(reward.requiredProductExternalIds[0]).trim()
        : null
    const productSku = fromItems ?? (fromRequired && fromRequired.length > 0 ? fromRequired : null)
    const freeQty = reward.voucherDiscountAmount != null ? Number(reward.voucherDiscountAmount) : NaN
    if (productSku && Number.isInteger(freeQty) && freeQty > 0) {
      targets = [{ sku: productSku, quantity: freeQty }]
      value = freeQty
    } else {
      value = reward.value ?? null
    }
  } else if (slugBelongsTo('percentDiscount', reward.rewardType?.slug)) {
    value = reward.value ?? reward.discountValuePercent ?? null
  } else if (slugBelongsTo('discount', reward.rewardType?.slug)) {
    value = reward.voucherDiscountAmount ?? reward.value ?? null
  } else {
    value = reward.value ?? null
  }

  const payload: RewardSavePayload = {
    id: reward.id ?? null,
    name: reward.name,
    description: reward.description?.trim() || null,
    rewardTypeId,
    source: reward.source ?? 'offer',
    usages: [...new Set([...(reward.usages ?? []), 'offer'])],
    storeIds,
    targets,
    value,
    minimumOrderAmount: reward.minimumOrderAmount ?? null,
    validityInDays: reward.validityInDays ?? null,
    claimLimitQuantity: (reward.claimLimitQuantity ?? 0) > 0 ? reward.claimLimitQuantity : null,
    claimLimitDays: reward.claimLimitDays ?? null,
    isForSingleUse: reward.isForSingleUse ?? false,
    conditions
  }

  const rewardItems = buildRewardItemsSavePayload(reward)
  if (rewardItems.length > 0) {
    payload.items = rewardItems
  }

  if (
    ['voucher_discount_percent', 'voucher_discount_amount'].includes(reward.rewardType?.slug) &&
    ctx.applyReductionOn !== 'products'
  ) {
    payload.items = []
    payload.externalId = ''
  }

  if (reward.isForSingleUse !== undefined) {
    payload.isForSingleUse = reward.isForSingleUse
  }

  if (reward.pictureUrl !== null) {
    payload.pictureUrl = reward.pictureUrl
  } else {
    delete payload.pictureUrl
  }

  if (isMerchantRewardProductSkuSlug(reward.rewardType?.slug)) {
    normalizeMerchantRewardSavePayload(payload as Record<string, unknown>, reward)
  }

  if (ctx.applyReductionOn === 'products') {
    applyOfferProductDiscountRewardTypeWhenSkusSelected(
      payload as { rewardTypeId?: number | null; externalId?: string | null; items?: unknown[] },
      reward.rewardType?.slug,
      ctx.rewardTypeIdsBySlug
    )
  }

  return payload
}
